import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { isEqual } from 'lodash'
import { NewTable } from '../../components/NewTable/NewTable'
import { fetchWarehouse } from '../../services/warehouseSevices'
import { fetchPrintJobs, printPrintJobByCode, reprintPrintJob } from '../../services/printJobServices'
import { printPdfSilently } from '../../helpers/printPdf'
import { buildLabelPdf, UNPRINTABLE_BARCODE_MESSAGE } from './labelPdf'
import { ambiguousCandidates, buildListParams, isRouteMissing, normalizeCode, resolvePrintJobError } from './printJobs'
import { applyFilterChange, buildColumns, buildRows, FILTERS_INITIAL_DATA, PAGE_SIZE } from './historyTable'
import { PrintScanBar } from './PrintScanBar'
import { StoreChoiceDialog } from './StoreChoiceDialog'

// A fallback tab is still navigating to our URL when printPdfSilently resolves, and an
// "Abrir etiqueta" tab loads it after window.open returns: both keep it alive this long.
const OPENED_URL_TTL_MS = 60000

const STYLES = {
  page: { background: '#F7F8FA' },
  header: { height: 55, padding: '16px 22px', borderBottom: '1px solid #E6E8F2' },
  title: { fontSize: 19, fontWeight: 700, letterSpacing: '-0.01em', color: '#07091A' },
  content: { padding: 22, gap: 22 },
  historyTitle: { fontSize: 14, fontWeight: 700, color: '#07091A', marginBottom: 10 },
  notice: { borderRadius: 12, border: '1px solid #E6E8F2', padding: '14px 18px', fontSize: 13, color: '#5A608A' },
  listError: { borderRadius: 8, padding: '11px 14px', fontSize: 13, fontWeight: 500, background: '#FBE1E5', color: '#B11829', marginBottom: 10 },
}

const retryTransportErrors = (failureCount, error) => !error?.response && failureCount < 2

export const PendingPrintsContainer = () => {
  const { t } = useTranslation()
  const { warehouse_id } = useParams()
  const warehouseId = Number(warehouse_id)
  const warehouseIsValid = Number.isInteger(warehouseId) && warehouseId > 0
  const queryClient = useQueryClient()

  const [searchParams, setSearchParams] = useState({ page: 1, per_page: PAGE_SIZE })
  const [filters, setFilters] = useState({})
  const [filtersData, setFiltersData] = useState(FILTERS_INITIAL_DATA)
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState(null)
  const [ambiguity, setAmbiguity] = useState(null)

  const filtersRef = useRef({})
  const inFlightRef = useRef(false)
  const mountedRef = useRef(true)
  const warehouseRef = useRef(warehouseId)
  const pendingUrlsRef = useRef(new Map())
  const scanInputRef = useRef(null)
  // Keeps the dialog's rows on screen while it fades out after the choice is cleared.
  const lastAmbiguityRef = useRef(null)
  warehouseRef.current = warehouseId
  if (ambiguity) lastAmbiguityRef.current = ambiguity

  useEffect(() => {
    setAmbiguity(null)
    setStatus(null)
  }, [warehouseId])

  useEffect(() => {
    const pendingUrls = pendingUrlsRef.current
    return () => {
      mountedRef.current = false
      pendingUrls.forEach((timer, url) => {
        clearTimeout(timer)
        URL.revokeObjectURL(url)
      })
      pendingUrls.clear()
    }
  }, [])

  const warehouseQuery = useQuery(['warehouse', warehouse_id], () => fetchWarehouse(warehouse_id), {
    enabled: warehouseIsValid,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
  const timeZone = warehouseQuery.data?.time_zone || null
  // Not while loading; a failed refetch keeps the zone already known.
  const timeZoneMissing = !timeZone && (warehouseQuery.isError || warehouseQuery.isSuccess)
  // A day range needs the warehouse's time zone: until it is known, no list is shown rather than an unfiltered one.
  const dateRangeOnHold = !!filters.lastPrinted && !timeZone

  const params = useMemo(() => buildListParams(searchParams, filters, timeZone), [searchParams, filters, timeZone])
  const listQuery = useQuery(['print_jobs', warehouseId, params], () => fetchPrintJobs(warehouseId, params), {
    enabled: warehouseIsValid && !dateRangeOnHold,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: retryTransportErrors,
  })
  const routeMissing = isRouteMissing(listQuery.error)

  const printMutation = useMutation(({ code, id }) => (id !== undefined ? reprintPrintJob(warehouseId, id) : printPrintJobByCode(warehouseId, code)))

  const revokeLater = useCallback(url => {
    const timer = setTimeout(() => {
      pendingUrlsRef.current.delete(url)
      URL.revokeObjectURL(url)
    }, OPENED_URL_TTL_MS)
    pendingUrlsRef.current.set(url, timer)
  }, [])

  const printFlow = async (request, onJobResolved) => {
    let job
    try {
      job = await printMutation.mutateAsync(request)
    } catch (error) {
      const candidates = request.code ? ambiguousCandidates(error) : null
      if (candidates) return { kind: 'choosing', code: request.code, candidates }
      // A job printed by id that is gone: refetch so the backend's list drops it.
      const jobGone = request.id !== undefined && error?.response?.status === 404 && error.response.data?.error_code === 'PRINT_JOB_NOT_FOUND'
      if (jobGone) queryClient.invalidateQueries(['print_jobs', warehouseId])
      return { kind: 'error', message: resolvePrintJobError(error, t, { requestedCode: request.code ?? request.barcode }) }
    }
    // The backend stamped last_printed_at already, whatever happens to the PDF below.
    queryClient.invalidateQueries(['print_jobs', warehouseId])

    // The copies hint has to be on screen before the modal print dialog opens.
    onJobResolved({ kind: 'printing', code: job.barcode, copies: job.copies })

    let blob
    let url
    try {
      blob = buildLabelPdf(job)
      url = URL.createObjectURL(blob)
    } catch (error) {
      const unprintable = error?.message === UNPRINTABLE_BARCODE_MESSAGE
      return { kind: 'error', message: t(unprintable ? 'pending_prints.errors.unprintable_barcode' : 'pending_prints.errors.generic') }
    }
    const outcome = await printPdfSilently(url, {
      onFallback: error => console.warn('PendingPrints -> print could not use the hidden iframe', error),
    })
    if (outcome === 'fallback') revokeLater(url)
    else URL.revokeObjectURL(url)
    return { kind: 'printed', outcome, code: job.barcode, copies: job.copies, blob }
  }

  const runPrint = async request => {
    if (inFlightRef.current) return
    inFlightRef.current = true
    const startedFor = warehouseId
    setStatus(null)
    setBusy(true)
    const isCurrent = () => mountedRef.current && warehouseRef.current === startedFor
    let next = null
    try {
      next = await printFlow(request, interim => {
        if (isCurrent()) setStatus(interim)
      })
    } catch (_) {
      next = { kind: 'error', message: t('pending_prints.errors.generic') }
    } finally {
      inFlightRef.current = false
      if (mountedRef.current) {
        const current = warehouseRef.current === startedFor
        const choosing = current && next?.kind === 'choosing'
        setStatus(current ? (choosing ? { kind: 'choosing' } : next) : null)
        setBusy(false)
        // After busy is released, so the scan bar keeps and selects the code before the dialog takes the focus.
        if (choosing) setAmbiguity({ code: next.code, candidates: next.candidates })
      }
    }
  }

  const handleScan = raw => {
    const code = normalizeCode(raw)
    if (code) runPrint({ code })
  }

  const handleReprint = job => runPrint({ id: job.id, barcode: job.barcode })

  // Same about:blank → opener = null → navigate sequence as printPdfSilently's fallback tab.
  const handleOpenLabel = () => {
    const url = URL.createObjectURL(status.blob)
    let tab = null
    try {
      tab = window.open('', '_blank')
    } catch (_) {
      tab = null
    }
    if (!tab) {
      URL.revokeObjectURL(url)
      return
    }
    try {
      tab.opener = null
      tab.location.href = url
    } catch (_) {
      // A tab that keeps its opener or refuses the navigation is closed, never left pointing at the WMS.
      try {
        tab.close()
      } catch (_) {
        /* nothing else to do about it */
      }
      URL.revokeObjectURL(url)
      return
    }
    revokeLater(url)
  }

  const handleType = useCallback(() => setStatus(null), [])

  const handleChooseStore = candidate => {
    const code = ambiguity?.code
    setAmbiguity(null)
    // By job id: two candidates can share a store, and an expired one is re-issued by the same call.
    if (code) runPrint({ id: candidate.id, barcode: candidate.barcode ?? code })
  }

  const handleCancelChoice = () => setAmbiguity(null)

  // After a choice the print owns the scan bar (it refocuses when done); after Cancelar the code is offered again.
  const handleChoiceLeft = () => {
    if (inFlightRef.current) return
    scanInputRef.current?.focus()
    scanInputRef.current?.select()
  }

  const handleSortChange = sortBy => {
    const sort = sortBy?.[0]
    const order_by = sort?.id
    const order = sort ? (sort.desc ? 'desc' : 'asc') : undefined
    setSearchParams(prev => {
      if (prev.order_by === order_by && prev.order === order) return prev
      const next = { ...prev, page: 1 }
      delete next.order_by
      delete next.order
      return order_by ? { ...next, order_by, order } : next
    })
  }

  const handleFilterChange = (updatedData, key) => {
    setFiltersData(prev => prev.map(item => (item.key === key ? { ...item, data: updatedData } : item)))
    const next = applyFilterChange(filtersRef.current, key, updatedData)
    if (isEqual(next, filtersRef.current)) return
    filtersRef.current = next
    setFilters(next)
    setSearchParams(prev => (prev.page === 1 ? prev : { ...prev, page: 1 }))
  }

  const handleResetFilters = () => {
    filtersRef.current = {}
    setFilters({})
    // A fresh array: Filter only re-syncs (showing the default filters again) when the reference changes.
    setFiltersData([...FILTERS_INITIAL_DATA])
    setSearchParams(prev => (prev.page === 1 ? prev : { ...prev, page: 1 }))
  }

  const handlePageSizeChange = option => {
    const per_page = Number(option.id)
    setSearchParams(prev => (prev.per_page === per_page ? prev : { ...prev, per_page, page: 1 }))
  }

  const columns = useMemo(() => buildColumns(), [])
  // A held list still has the unfiltered data in react-query: none of it is shown.
  const listData = dateRangeOnHold ? undefined : listQuery.data
  const jobs = listData?.print_jobs
  const rows = useMemo(
    () => buildRows(jobs, { warehouseId: warehouse_id, timeZone, t, onReprint: handleReprint, reprintDisabled: busy }),
    // handleReprint reads warehouseId and t, both covered here (warehouse_id, t); the rest are refs and setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jobs, warehouse_id, timeZone, t, busy],
  )

  const listError = listQuery.error && !routeMissing ? resolvePrintJobError(listQuery.error, t) : null

  return (
    <div className="flex flex-col flex-grow" style={STYLES.page}>
      <header className="flex flex-shrink-0 items-center bg-white" style={STYLES.header}>
        <h1 style={STYLES.title}>{t('pending_prints.title')}</h1>
      </header>
      <div className="flex flex-col" style={STYLES.content}>
        <PrintScanBar
          onSubmit={handleScan}
          onType={handleType}
          busy={busy}
          disabled={routeMissing || !warehouseIsValid}
          status={status}
          onOpenLabel={handleOpenLabel}
          inputRef={scanInputRef}
        />
        <StoreChoiceDialog
          open={!!ambiguity}
          code={lastAmbiguityRef.current?.code}
          candidates={lastAmbiguityRef.current?.candidates}
          timeZone={timeZone}
          onChoose={handleChooseStore}
          onCancel={handleCancelChoice}
          afterLeave={handleChoiceLeft}
        />
        <section>
          <h3 style={STYLES.historyTitle}>{t('pending_prints.history.title')}</h3>
          {routeMissing ? (
            <div className="bg-white" style={STYLES.notice}>
              {t('pending_prints.unavailable')}
            </div>
          ) : (
            <>
              {timeZoneMissing && (
                <div role="alert" style={STYLES.listError}>
                  {t('pending_prints.errors.time_zone')}
                </div>
              )}
              {listError && (
                <div role="alert" style={STYLES.listError}>
                  {listError}
                </div>
              )}
              <NewTable
                data={rows}
                columns={columns}
                cursorPointer={false}
                showLoader={listQuery.isFetching && listQuery.isPreviousData}
                isFetching={listQuery.isFetching}
                showPaginationOnFooter
                emptyTableText={listQuery.isLoading || listError || dateRangeOnHold ? null : t('pending_prints.empty')}
                paginationMeta={listData?.meta || { per_page: searchParams.per_page, current_page: searchParams.page }}
                onPaginationChange={page => setSearchParams(prev => ({ ...prev, page }))}
                onPageSizeChange={handlePageSizeChange}
                onSortChange={handleSortChange}
                onSearchHandler={handleFilterChange}
                handleResetFilters={handleResetFilters}
                filtersData={filtersData}
                footerText={t('pending_prints.footer')}
              />
            </>
          )}
        </section>
      </div>
    </div>
  )
}
